import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';

const ViewBlogs = () => {
  const { user } = useAuth();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState('');
  const [pending, setPending] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [recordsPerPage, setRecordsPerPage] = useState(10);

  const canEdit = user?.role === 'admin' || user?.permissions?.some(p => 
    p.page === 'blog' && p.modules?.some(m => m.name === 'blog' && m.actions?.includes('edit'))
  );

  const canAdd = user?.role === 'admin' || user?.permissions?.some(p => 
    p.page === 'blog' && p.modules?.some(m => m.name === 'blog' && m.actions?.includes('add'))
  );

  const fetchBlogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        draw: 1,
        start: (currentPage - 1) * recordsPerPage,
        length: recordsPerPage,
        search: keyword,
        pending: pending,
        type: typeFilter
      };
      
      const response = await apiService.post('blogs', params);
      if (response && response.data) {
        setBlogs(response.data);
        setTotalRecords(response.recordsFiltered || response.recordsTotal || 0);
      }
    } catch (error) {
      toast.error('Failed to load blogs');
      console.error('Blogs fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, recordsPerPage, keyword, pending, typeFilter]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchBlogs();
    }, keyword ? 500 : 0);
    
    return () => clearTimeout(timeoutId);
  }, [fetchBlogs]);

  const toggleBlogStatus = async (id) => {
    try {
      const response = await apiService.get(`blogStatus/${id}`);
      if (!response.success) {
        toast.success('Status updated successfully');
        setBlogs(prevBlogs => 
          prevBlogs.map(blog => 
            blog._id === id ? { ...blog, status: blog.status === 1 ? 0 : 1 } : blog
          )
        );
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const toggleTopStatus = async (id) => {
    try {
      const response = await apiService.get(`blogTopStatus/${id}`);
      if (response.success) {
        toast.success(response.message);
        setBlogs(prevBlogs => 
          prevBlogs.map(blog => 
            blog._id === id ? { ...blog, top: !blog.top } : blog
          )
        );
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error('Failed to update top status');
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-IN'),
      time: date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
    };
  };

  const totalPages = Math.ceil(totalRecords / recordsPerPage);

  return (
    <div className="p-0">
      <nav className="flex flex-wrap items-center text-xs sm:text-sm mb-2">
        <Link to="/dashboard" className="text-gray-500 hover:text-gray-700">Home</Link>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-gray-900 font-medium">Blog</span>
      </nav>

      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-4 sm:p-6 border-b flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:flex-1">
            <div className="relative flex-1 min-w-[200px]">
              <i className="bi bi-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                value={keyword}
                onChange={(e) => { setKeyword(e.target.value); setCurrentPage(1); }}
                placeholder="Search query here.."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <select
              value={pending}
              onChange={(e) => { setPending(e.target.value); setCurrentPage(1); }}
              className="w-full sm:w-full sm:w-[150px] px-2 sm:px-4 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="All">Show All</option>
              <option value="1">Content</option>
              <option value="0">No Meta Data</option>
            </select>
            <select
              value={typeFilter}
              onChange={(e) => { setTypeFilter(e.target.value); setCurrentPage(1); }}
              className="w-full sm:w-full sm:w-[160px] px-2 sm:px-4 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="All">All Types</option>
              <option value="travellinglark">TravellingLark</option>
              <option value="woltrix">Woltrix</option>
            </select>
          </div>
          {canAdd && (
            <Link to="/add-blog" className="w-full sm:w-auto px-2 sm:px-4 py-2 bg-[#009ef7] text-white rounded-lg hover:bg-[#009ef7]/90 no-underline text-center whitespace-nowrap">
              <i className="bi bi-plus-lg mr-2"></i>Add Blog
            </Link>
          )}
        </div>

        <div className="p-4 sm:p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#009ef7]"></div>
              <span className="ml-2 text-gray-600">Loading...</span>
            </div>
          ) : blogs.length === 0 ? (
            <div className="text-center py-12">
              <i className="bi bi-journal-text text-4xl text-gray-300 mb-2"></i>
              <h3 className="text-lg font-medium text-gray-900 mb-1">No Blogs Found</h3>
              <p className="text-gray-500">No blogs match your criteria.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs sm:text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left text-gray-500 text-xs uppercase">
                    <th className="px-2 sm:px-4 py-3">Index</th>
                    <th className="px-2 sm:px-4 py-3" style={{ minWidth: '150px' }}>Image</th>
                    <th className="px-2 sm:px-4 py-3">Title</th>
                    <th className="px-2 sm:px-4 py-3">Description</th>
                    <th className="px-2 sm:px-4 py-3">Type</th>
                    {canEdit && (
                      <>
                        <th className="px-2 sm:px-4 py-3">Publish</th>
                        <th className="px-2 sm:px-4 py-3">Top</th>
                      </>
                    )}
                    <th className="px-2 sm:px-4 py-3">View</th>
                    <th className="px-2 sm:px-4 py-3">Created At</th>
                    <th className="px-2 sm:px-4 py-3">Updated At</th>
                    {canEdit && <th className="px-2 sm:px-4 py-3">Action</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {blogs.map((blog, index) => {
                    const created = formatDateTime(blog.createdAt);
                    const updated = formatDateTime(blog.updatedAt);
                    return (
                      <tr key={blog._id} className="hover:bg-gray-50">
                        <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm">{(currentPage - 1) * recordsPerPage + index + 1}</td>
                        <td className="px-2 sm:px-4 py-3" style={{ minWidth: '150px' }}>
                          <img
                            src={blog.image ? `${process.env.REACT_APP_MEDIA_URL}${blog.image}` : '/assets/blank-image.svg'}
                            alt={blog.title}
                            className="h-16 sm:h-24 w-32 sm:w-48 rounded-lg object-cover"
                          />
                        </td>
                        <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm">{blog.title}</td>
                        <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm">{blog.sortDescription}</td>
                        <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm">{blog.type || 'travellinglark'}</td>
                        {canEdit && (
                          <>
                            <td className="px-2 sm:px-4 py-3">
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={blog.status === 1}
                                  onChange={() => toggleBlogStatus(blog._id)}
                                  className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                              </label>
                            </td>
                            <td className="px-2 sm:px-4 py-3">
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={blog.top === true}
                                  onChange={() => toggleTopStatus(blog._id)}
                                  className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                              </label>
                            </td>
                          </>
                        )}
                        <td className="px-2 sm:px-4 py-3">
                          <a
                            href={(blog.type || '').toLowerCase() === 'woltrix' ? 'https://www.woltrixlab.com/blog/' + blog.slug : 'https://travellinglark.com/blog/' + blog.slug}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block px-2 sm:px-3 py-1 bg-[#009ef7]/10 text-[#009ef7] rounded no-underline"
                          >
                            <i className="bi bi-eye"></i>
                          </a>
                        </td>
                        <td className="px-2 sm:px-4 py-3">
                          <div className="flex flex-col">
                            <span className="text-gray-800 font-semibold text-xs sm:text-sm">{created.date}</span>
                            <span className="text-gray-500 text-xs">{created.time}</span>
                          </div>
                        </td>
                        <td className="px-2 sm:px-4 py-3">
                          <div className="flex flex-col">
                            <span className="text-gray-800 font-semibold text-xs sm:text-sm">{updated.date}</span>
                            <span className="text-gray-500 text-xs">{updated.time}</span>
                          </div>
                        </td>
                        {canEdit && (
                          <td className="px-2 sm:px-4 py-3">
                            <Link
                              to={`/edit-blog/${blog._id}`}
                              className="inline-flex items-center justify-center w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded no-underline"
                            >
                              <i className="bi bi-pencil text-green-600"></i>
                            </Link>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {blogs.length > 0 && (
          <div className="bg-gray-50 px-4 sm:px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs sm:text-sm text-gray-700">Show</span>
              <select
                value={recordsPerPage}
                onChange={(e) => { setRecordsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                className="px-3 py-1 border border-gray-300 rounded-lg text-xs sm:text-sm"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-xs sm:text-sm text-gray-700">entries</span>
            </div>
            <div className="flex items-center gap-1 flex-wrap justify-center">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded text-xs sm:text-sm disabled:opacity-50 hover:bg-gray-100"
              >«</button>
              {[...Array(totalPages)].map((_, i) => {
                const page = i + 1;
                if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className="px-3 py-1 border rounded text-xs sm:text-sm"
                      style={{
                        backgroundColor: currentPage === page ? '#009ef7' : '#fff',
                        borderColor: currentPage === page ? '#009ef7' : '#d1d5db',
                        color: currentPage === page ? '#fff' : '#374151'
                      }}
                    >{page}</button>
                  );
                } else if (page === currentPage - 2 || page === currentPage + 2) {
                  return <span key={page} className="px-2 text-gray-500">...</span>;
                }
                return null;
              })}
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded text-xs sm:text-sm disabled:opacity-50 hover:bg-gray-100"
              >»</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewBlogs;



